import React, { useState, useEffect } from 'react';
import axios from 'axios';
import showToast from '../../utils/toastHelper';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, completed, failed
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });

  useEffect(() => {
    fetchTransactions();
    
    // Auto-refresh every 10 seconds for real-time transaction updates
    const interval = setInterval(() => {
      fetchTransactions();
    }, 10000);
    
    // Cleanup on unmount
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, pagination.page, pagination.limit]);

  const fetchTransactions = async () => {
    try {
      const token = localStorage.getItem('token');
      const statusParam = filter !== 'all' ? `&status=${filter}` : '';
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/payments/transactions?page=${pagination.page}&limit=${pagination.limit}${statusParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Force new object reference to trigger re-render
      setTransactions([...(response.data.data?.transactions || [])]);
      setPagination(prev => ({
        ...prev,
        total: response.data.data?.pagination?.total || 0,
        totalPages: response.data.data?.pagination?.totalPages || 0,
      }));
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      // Only show error toast on initial load
      if (loading) {
        showToast('Failed to load transactions', 'error');
      }
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'warning',
      successful: 'success',
      completed: 'success',
      failed: 'danger',
      refunded: 'info',
      cancelled: 'secondary',
    };
    return `badge bg-${badges[status] || 'secondary'}`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAmount = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'UGX',
    }).format(amount);
  };

  const filteredTransactions = transactions
    .filter(t => 
      searchTerm === '' || 
      t.user?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.flutterwaveTxRef?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.flutterwaveTxId?.toLowerCase().includes(searchTerm.toLowerCase())
    );

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="mb-4">
        <h3 className="mb-1" style={{ fontWeight: '600' }}>Transactions</h3>
        <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
          Track all vote payments and revenue
        </p>
      </div>

      {/* Filters and Search */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3 mb-md-0">
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('all')}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${filter === 'successful' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('successful')}
                >
                  Successful
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${filter === 'pending' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('pending')}
                >
                  Pending
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${filter === 'failed' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('failed')}
                >
                  Failed
                </button>
              </div>
            </div>
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Search by name or reference..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="card">
        <div className="card-body">
          {filteredTransactions.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-receipt" style={{ fontSize: '3rem', color: '#d1d5db' }}></i>
              <p className="mt-3 text-muted">No transactions found</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Reason</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((transaction) => (
                    <tr key={transaction.id}>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        <div>{transaction.flutterwaveTxRef || '-'}</div>
                        {transaction.flutterwaveTxId && (
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            TX: {transaction.flutterwaveTxId}
                          </div>
                        )}
                      </td>
                      <td style={{ fontWeight: '600' }}>
                        {formatAmount(transaction.amount, transaction.currency)}
                      </td>
                      <td>
                        <span className={getStatusBadge(transaction.status)} style={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>
                          {transaction.status}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        {transaction.reason ? (
                          <span title={transaction.reason}>
                            {transaction.reason.length > 40 ? `${transaction.reason.substring(0, 40)}...` : transaction.reason}
                          </span>
                        ) : (
                          <span style={{ color: '#d1d5db' }}>-</span>
                        )}
                      </td>
                      <td style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        {formatDate(transaction.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mt-4">
          <div className="d-flex align-items-center gap-3">
            <div className="text-muted" style={{ fontSize: '0.875rem' }}>
              Showing {Math.min((pagination.page - 1) * pagination.limit + 1, pagination.total)} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} transactions
            </div>
            <div className="d-flex align-items-center gap-2">
              <label htmlFor="pageSize" className="text-muted mb-0" style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Per page:</label>
              <select
                id="pageSize"
                className="form-select form-select-sm"
                value={pagination.limit}
                onChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                style={{ width: 'auto' }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
          {pagination.totalPages > 1 && (
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  Previous
                </button>
              </li>
              {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                const pageNum = pagination.page <= 3 ? i + 1 : pagination.page - 2 + i;
                if (pageNum > pagination.totalPages) return null;
                return (
                  <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                    >
                      {pageNum}
                    </button>
                  </li>
                );
              })}
              <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
          )}
        </div>
      )}
    </div>
  );
};

export default Transactions;
